import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import ConfirmDialog from "../../components/ConfirmDialog";
import useConfirmDialog from "../../hooks/useConfirmDialog";

const DEFAULT_FILTERS = Object.freeze({
  search: "",
  companyId: "",
  userId: "",
  action: "",
  success: "",
  startDate: "",
  endDate: "",
});

const METHOD_COLORS = {
  GET:    { bg: "rgba(59,130,246,0.16)", color: "#93c5fd" },
  POST:   { bg: "rgba(37,211,102,0.14)", color: "#7de8a8" },
  PUT:    { bg: "rgba(245,181,68,0.16)", color: "#f5cd83" },
  PATCH:  { bg: "rgba(245,181,68,0.16)", color: "#f5cd83" },
  DELETE: { bg: "rgba(239,90,90,0.16)", color: "#ff9d9d" },
};

function MethodBadge({ method }) {
  if (!method) return null;
  const m = method.toUpperCase();
  const style = METHOD_COLORS[m] || { bg: "rgba(255,255,255,0.08)", color: "#aab0c4" };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.04em",
      background: style.bg,
      color: style.color,
      fontFamily: "monospace",
      marginRight: 6,
    }}>
      {m}
    </span>
  );
}

function StatusBadge({ code, success }) {
  if (!code) return <span style={{ color: "var(--pf-text-muted)" }}>—</span>;
  const c = Number(code);
  let bg, color;
  if (c >= 500)      { bg = "rgba(239,90,90,0.16)"; color = "#ff9d9d"; }
  else if (c >= 400) { bg = "rgba(245,181,68,0.16)"; color = "#f5cd83"; }
  else if (c >= 300) { bg = "rgba(245,181,68,0.14)"; color = "#f5cd83"; }
  else               { bg = "rgba(37,211,102,0.14)"; color = "#7de8a8"; }
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 9px",
      borderRadius: 6,
      fontSize: "0.82rem",
      fontWeight: 700,
      background: bg,
      color,
    }}>
      {code}
    </span>
  );
}

export default function OpsAuditLogs() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [lookups, setLookups] = useState({ companies: [], users: [] });
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { confirmProps, openConfirm } = useConfirmDialog();

  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError("");
      const params = { page, pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await axios.get(`${API_BASE_URL}/ops/audit-logs`, { headers, params });
      const rows = data.data || [];
      setLogs(rows);
      setPagination(data.pagination || { total: 0, totalPages: 0 });
      setLookups(data.lookups || { companies: [], users: [] });
      setSelectedIds((prev) => {
        if (prev.size === 0) return prev;
        const valid = new Set(rows.filter((r) => prev.has(r.id)).map((r) => r.id));
        return valid.size === prev.size ? prev : valid;
      });
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load audit logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filterKey]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const clearFilters = () => { setFilters({ ...DEFAULT_FILTERS }); setPage(1); };
  const handlePageSizeChange = (e) => { setPageSize(Number(e.target.value)); setPage(1); };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(logs.map((l) => l.id)) : new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      setDeleting(true);
      setError("");
      await axios.post(
        `${API_BASE_URL}/ops/audit-logs/bulk-delete`,
        { ids: Array.from(selectedIds) },
        { headers }
      );
      setSelectedIds(new Set());
      fetchLogs();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete audit logs.");
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    openConfirm({
      title: "Delete Audit Entries",
      message: `Delete ${selectedIds.size} entr${selectedIds.size === 1 ? "y" : "ies"}? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: handleDeleteSelected,
    });
  };

  const totalPages = pagination.totalPages ?? 0;
  const disablePrev = page <= 1 || totalPages === 0;
  const disableNext = totalPages === 0 || page >= totalPages;
  const allVisibleSelected = logs.length > 0 && logs.every((l) => selectedIds.has(l.id));
  const someSelected = selectedIds.size > 0 && !allVisibleSelected;

  return (
    <div className="ops-section">
      <div className="ops-section-header">
        <div>
          <h3 className="mb-1">Audit Trail</h3>
          <p className="text-muted mb-0">
            All API activity across companies — filter by company to diagnose issues.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <label className="text-muted small mb-0">Rows</label>
          <select className="form-select w-auto" value={pageSize} onChange={handlePageSizeChange}>
            {[25, 50, 100, 200].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn btn-outline-secondary" onClick={fetchLogs} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            className="btn btn-outline-danger"
            disabled={selectedIds.size === 0 || deleting}
            onClick={confirmDeleteSelected}
          >
            {deleting ? "Deleting…" : `Delete (${selectedIds.size})`}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="audit-filter-grid">
        <div>
          <label>Search</label>
          <input
            type="text"
            name="search"
            className="form-control"
            placeholder="Path, email, error message…"
            value={filters.search}
            onChange={handleFilterChange}
          />
        </div>
        <div>
          <label>Company</label>
          <select name="companyId" className="form-select" value={filters.companyId} onChange={handleFilterChange}>
            <option value="">All companies</option>
            {lookups.companies.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>User</label>
          <select name="userId" className="form-select" value={filters.userId} onChange={handleFilterChange}>
            <option value="">All users</option>
            {lookups.users.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.first_name} {u.last_name} ({u.email})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Action / Path</label>
          <input
            type="text"
            name="action"
            className="form-control"
            placeholder="e.g. request, login…"
            value={filters.action}
            onChange={handleFilterChange}
          />
        </div>
        <div>
          <label>Result</label>
          <select name="success" className="form-select" value={filters.success} onChange={handleFilterChange}>
            <option value="">All</option>
            <option value="false">Errors only</option>
            <option value="true">Success only</option>
          </select>
        </div>
        <div>
          <label>Start</label>
          <input type="datetime-local" name="startDate" className="form-control" value={filters.startDate} onChange={handleFilterChange} />
        </div>
        <div>
          <label>End</label>
          <input type="datetime-local" name="endDate" className="form-control" value={filters.endDate} onChange={handleFilterChange} />
        </div>
      </div>

      <div className="d-flex justify-content-between flex-wrap gap-3 align-items-center mb-3">
        <div className="text-muted small">
          {loading ? "Loading…" : `${pagination.total.toLocaleString()} entries${filters.companyId ? " for this company" : ""}`}
        </div>
        <div className="d-flex gap-3">
          <button className="btn btn-link p-0" onClick={clearFilters} disabled={loading}>Clear filters</button>
          <button className="btn btn-outline-secondary" onClick={fetchLogs} disabled={loading}>Apply</button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="ops-table-wrapper">
        <table className="ops-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </th>
              <th>Time</th>
              <th>Company</th>
              <th>User</th>
              <th>Request</th>
              <th>Status</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <React.Fragment key={log.id}>
                <tr
                  className={selectedIds.has(log.id) ? "selected-row" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(log.id)}
                      onChange={() => toggleSelect(log.id)}
                    />
                  </td>
                  <td>
                    <div className="audit-log-meta">
                      <strong style={{ fontSize: "0.85rem" }}>
                        {new Date(log.created_at).toLocaleString()}
                      </strong>
                      {log.latency_ms != null && (
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>{log.latency_ms}ms</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="audit-log-meta">
                      <strong style={{ fontSize: "0.85rem" }}>{log.company_name || "—"}</strong>
                      {log.company_id && <span className="mono-id">{log.company_id}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="audit-log-meta">
                      {log.first_name || log.last_name
                        ? <strong style={{ fontSize: "0.85rem" }}>{`${log.first_name || ""} ${log.last_name || ""}`.trim()}</strong>
                        : <span className="text-muted">{log.actor_type || "system"}</span>
                      }
                      {log.email && <span className="text-muted" style={{ fontSize: "0.75rem" }}>{log.email}</span>}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                      <MethodBadge method={log.http_method} />
                      <span style={{ fontFamily: "monospace", fontSize: "0.82rem", wordBreak: "break-all" }}>
                        {log.path || log.action || "—"}
                      </span>
                    </div>
                  </td>
                  <td>
                    <StatusBadge code={log.status_code} success={log.success} />
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    {log.error_message
                      ? <span style={{ color: "#ff9d9d", fontSize: "0.82rem" }}>{log.error_message}</span>
                      : <span className="text-muted">—</span>
                    }
                  </td>
                </tr>
                {expanded === log.id && (
                  <tr>
                    <td colSpan={7} style={{ background: "var(--pf-background)", padding: "14px 16px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 12 }}>
                        <div>
                          <span className="text-muted small">Method &amp; Path</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                            <MethodBadge method={log.http_method} />
                            <span className="mono-id">{log.path || log.action || "—"}</span>
                          </div>
                        </div>
                        <div>
                          <span className="text-muted small">Status Code</span>
                          <div style={{ marginTop: 4 }}><StatusBadge code={log.status_code} success={log.success} /></div>
                        </div>
                        <div>
                          <span className="text-muted small">Result</span>
                          <div style={{ marginTop: 4, fontWeight: 600, color: log.success === false ? "#dc2626" : "#16a34a" }}>
                            {log.success === false ? "Failed" : log.success === true ? "Success" : "—"}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted small">Latency</span>
                          <div style={{ marginTop: 4 }}>{log.latency_ms != null ? `${log.latency_ms}ms` : "—"}</div>
                        </div>
                        <div>
                          <span className="text-muted small">Company</span>
                          <div style={{ marginTop: 4 }}>{log.company_name || "—"}</div>
                          {log.company_id && <div className="mono-id">{log.company_id}</div>}
                        </div>
                        <div>
                          <span className="text-muted small">User</span>
                          <div style={{ marginTop: 4 }}>
                            {log.first_name || log.last_name
                              ? `${log.first_name || ""} ${log.last_name || ""}`.trim()
                              : log.actor_type || "—"}
                          </div>
                          {log.email && <div className="mono-id">{log.email}</div>}
                        </div>
                        <div>
                          <span className="text-muted small">IP Address</span>
                          <div className="mono-id" style={{ marginTop: 4 }}>{log.ip || "—"}</div>
                        </div>
                        <div>
                          <span className="text-muted small">Target</span>
                          <div style={{ marginTop: 4 }}>{log.target_type || "—"}</div>
                          {log.target_id && <div className="mono-id">{log.target_id}</div>}
                        </div>
                        <div>
                          <span className="text-muted small">Log ID</span>
                          <div className="mono-id" style={{ marginTop: 4 }}>{log.id}</div>
                        </div>
                      </div>
                      {log.error_message && (
                        <div style={{ marginBottom: 10, padding: "10px 14px", background: "rgba(239,90,90,0.12)", borderRadius: 8, borderLeft: "3px solid #ef5a5a" }}>
                          <div className="text-muted small" style={{ marginBottom: 3 }}>Error Message</div>
                          <span style={{ color: "#ff9d9d", fontSize: "0.88rem", fontWeight: 500 }}>{log.error_message}</span>
                        </div>
                      )}
                      {log.metadata && (
                        <>
                          <div className="text-muted small" style={{ marginBottom: 4 }}>Request Parameters</div>
                          <pre className="audit-log-detail">{JSON.stringify(log.metadata, null, 2)}</pre>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {logs.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="text-center text-muted">No audit entries match your filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="audit-pagination">
        <span className="text-muted small">
          Page {totalPages > 0 ? `${page} of ${totalPages}` : "0 of 0"}
        </span>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={disablePrev || loading}>
            Previous
          </button>
          <button className="btn btn-outline-secondary" onClick={() => setPage((p) => p + 1)} disabled={disableNext || loading}>
            Next
          </button>
        </div>
      </div>

      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
